export enum DateFilterType {
  EVENT = "EVENT",
  CREATION = "CREATION",
}

export enum MetricType {
  BILLING = "BILLING",
  REVENUE = "REVENUE",
}

export enum ProjectSortField {
  MM_CODE = "mmCode",
  CLIENT_NAME = "clientName",
  EVENT_NAME = "eventName",
}

export enum BudgetDashboardSortField {
  MM_CODE = "mmCode",
  CLIENT_NAME = "clientName",
  CELL_NAME = "cellName",
  DATE = "date",
  STATUS = "status",
  BUDGET_COUNT = "budgetCount",
}

export enum SortOrder {
  ASC = "asc",
  DESC = "desc",
}

export enum PeriodType {
  WEEKS = "weeks",
  MONTHS = "months",
}

export interface DashboardFiltersDto {
  startDate?: {
    start?: any;
    end?: any;
  };
  endDate?: {
    start?: any;
    end?: any;
  };
  dateFilterType?: DateFilterType;
  status?: string[];
  clientId?: string;
  mmCodes?: string[];
  metricType?: MetricType;
  cellId?: string;
  collaboratorId?: string;
  searchQuery?: string;
  sortBy?: ProjectSortField;
  sortOrder?: SortOrder;

  // Filtros de Evento e Concorrência
  eventScope?: string;
  hasCompetitor?: boolean;

  // Novos campos para o dashboard CEO
  periodType?: PeriodType;
  periodValue?: number;
  billingMin?: number;
  billingMax?: number;
  revenueMin?: number;
  revenueMax?: number;
  audienceMin?: number;
  audienceMax?: number;
  includeExtraFields?: boolean;
  page?: number;
  pageSize?: number;

  debugSessionId?: string;
}

export interface BudgetDashboardFiltersDto {
  clientId?: string;
  cellId?: string;
  status?: string[];
  startDate?: string;
  endDate?: string;
  onlyWithBudget?: boolean;
  staleOnly?: boolean;
  searchQuery?: string;
  sortBy?: BudgetDashboardSortField;
  sortOrder?: SortOrder;
  page?: number;
  pageSize?: number;
  limit?: number;
}

export interface BudgetDashboardProjectItem {
  id: string;
  mmCode: number;
  yearCode: number;
  clientId?: string;
  clientName?: string;
  cellId?: string;
  cellName?: string;
  date?: string;
  statusCode?: string;
  statusName?: string;
  budgetCount: number;
  hasBudget: boolean;
  daysSinceCreation: number | null;
  isStale: boolean;
}

export interface BudgetDashboardProjectResponse {
  total: number;
  data: BudgetDashboardProjectItem[];
}

export interface BudgetDashboardPeriodSummary {
  startDate: string;
  endDate: string;
  totalProjects: number;
  withBudget: number;
  adoptionRate: number;
}

export interface BudgetDashboardSummary {
  totalProjects: number;
  withBudget: number;
  withoutBudget: number;
  totalBudgets: number;
  adoptionRate: number;
  staleCount: number;
  staleDays: number;
  previousPeriod: BudgetDashboardPeriodSummary | null;
  adoptionDelta: number | null;
}

export interface BudgetDashboardMonthlyItem {
  key: string;
  label: string;
  projects: number;
  budgets: number;
  projectsWithBudget: number;
  adoptionRate: number;
}

export interface BudgetDashboardGroupItem {
  id: string | null;
  name: string;
  totalProjects: number;
  withBudget: number;
  withoutBudget: number;
  adoptionRate: number;
}

export interface BudgetDashboardOption {
  id: string;
  name: string;
  taxId?: string;
  nickname?: string;
}

export interface BudgetDashboardOptions {
  clients: BudgetDashboardOption[];
  cells: BudgetDashboardOption[];
  statuses: BudgetDashboardOption[];
}

export interface EventControlOption {
  id: string;
  name: string;
  count: number;
}

export interface EventControlOptions {
  clients: EventControlOption[];
  cells: EventControlOption[];
  statuses: EventControlOption[];
}

export interface EventControlRankingItem {
  id: string;
  name: string;
  value: number;
  percentage: number;
}

export interface EventControlRankings {
  billingByCell: EventControlRankingItem[];
  revenueByCell: EventControlRankingItem[];
  revenueByCollaborator: EventControlRankingItem[];
  billingByClient: EventControlRankingItem[];
  revenueByClient: EventControlRankingItem[];
}

export interface EventControlChartSerie {
  key: string;
  label: string;
  type: "bar" | "line";
  data: number[];
  backgroundColor?: string;
  borderColor?: string;
  borderDash?: number[];
}

export interface EventControlMmsByMonth {
  labels: string[];
  currentYear: number;
  previousYear: number;
  hasFilter: boolean;
  general: {
    currentYear: number[];
    previousYear: number[];
  };
  filtered?: {
    currentYear: number[];
    previousYear: number[];
  };
  series: EventControlChartSerie[];
}

export interface EventControlMonthlyCardItem {
  key: string;
  label: string;
  value: number;
}

export interface EventControlMonthlyCard {
  total: number;
  months: EventControlMonthlyCardItem[];
}

export interface EventControlMonthlyCards {
  year: number;
  mms: EventControlMonthlyCard;
  audited: EventControlMonthlyCard;
}

export interface PhaseMetrics {
  phase: string;
  count: number;
  billing: number;
  revenue: number;
  billingPercentage: number;
  revenuePercentage: number;
}

export interface DashboardSummary {
  _id: null;
  expectedBilling: number;
  expectedBillingYesterday?: number;
  expectedBillingGrowth?: number;
  expectedBillingGrowthPercentage?: number;
  expectedRevenue: number;
  expectedRevenueYesterday?: number;
  expectedRevenueGrowth?: number;
  expectedRevenueGrowthPercentage?: number;
  totalMMs: number;
  totalMMsYesterday?: number;
  totalMMsGrowth?: number;
  totalMMsGrowthPercentage?: number;
  totalAudited: number;
  totalAuditedYesterday?: number;
  totalAuditedGrowth?: number;
  totalAuditedGrowthPercentage?: number;
  phases?: PhaseMetrics[];
}

export interface MonthlyMM {
  month: string;
  count: number;
}

export interface BillingVsRevenue {
  billing: number;
  revenue: number;
  percentage: number;
}

export interface CellMetrics {
  cellId: string;
  cellName: string;
  value: number;
  billing: number;
  revenue: number;
}

export interface CollaboratorMetrics {
  collaboratorId: string;
  collaboratorName: string;
  value: number;
  billing: number;
  revenue: number;
}

export interface ClientMetrics {
  clientId: string;
  clientName: string;
  billing: number;
  revenue: number;
}

export interface ProjectStatusCount {
  id: string;
  name: string;
  count: number;
}

export interface UpcomingEvent {
  id: string;
  mmCode: string | number;
  yearCode?: number;
  eventName: string;
  clientName: string;
  date?: string;
  eventDate?: string;
  audience?: number;
  billing?: number;
  revenue?: number;
}

export interface UpcomingEventsData {
  byBilling: UpcomingEvent[];
  byRevenue: UpcomingEvent[];
  byAudience: UpcomingEvent[];
  periodLabel?: string;
}

export interface CEODashboardData {
  summary: DashboardSummary;
  upcomingEvents: UpcomingEventsData;
}

export interface BlacklistItem {
  _id: string;
  clientId: string;
  clientName?: string;
  nickname?: string;
  taxId?: string;
  createdAt: string;
}

export interface BlacklistOption {
  id: string;
  name: string;
}
